<template>
  <div class="fair-survey-step">
    <div class="fair-survey-step__head">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <div class="color-selection-step__grid">
      <button
        v-for="color in step.colors"
        :key="color.id"
        type="button"
        class="color-selection-step__item"
        :class="{
          'color-selection-step__item--selected': isColorSelected(color.id),
        }"
        :aria-pressed="isColorSelected(color.id)"
        @click="toggleColor(color.id)"
      >
        <span
          class="color-selection-step__swatch"
          :style="{ backgroundColor: color.hex }"
        >
          <FairIcon
            v-if="isColorSelected(color.id)"
            name="check"
            :size="16"
          />
        </span>
        <span class="color-selection-step__name">{{ color.name }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { useTasteClarificationModal } from '@/features/taste/store'
import type { TasteStep } from '@/features/taste/types/taste-config'

interface Props {
  step: TasteStep & { type: 'color_selection' }
}

const props = defineProps<Props>()
const tasteStore = useTasteClarificationModal()

const selectedColors = computed(() => {
  const data = tasteStore.tasteModal.tasteData[props.step.id] || []
  // Миграция: если в store есть старые данные (string названия/ID), преобразуем их в ID
  if (data.length > 0 && typeof data[0] === 'string') {
    return data.map((nameOrId: string) => {
      const color = props.step.colors.find(
        (c) => c.name === nameOrId || c.id.toString() === nameOrId
      )
      return color?.id
    }).filter((id: any) => id !== undefined)
  }
  return data
})

function isColorSelected(colorId: number): boolean {
  return selectedColors.value.includes(colorId)
}

function toggleColor(colorId: number) {
  const currentColors = [...selectedColors.value]
  const index = currentColors.indexOf(colorId)

  if (index > -1) {
    currentColors.splice(index, 1)
  } else {
    currentColors.push(colorId)
  }

  tasteStore.updateTasteData(props.step.id, currentColors)
}

onMounted(() => {
  // Initialize with existing data if any
  if (!tasteStore.tasteModal.tasteData[props.step.id]) {
    tasteStore.updateTasteData(props.step.id, [])
  }
})
</script>

<style scoped>
.color-selection-step__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: var(--fair-rd-space-12);
}

.color-selection-step__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--fair-rd-space-8);
  margin: 0;
  padding: var(--fair-rd-space-12) var(--fair-rd-space-8);
  border: 1px solid transparent;
  border-radius: var(--fair-rd-radius-card);
  background: transparent;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.color-selection-step__item:hover {
  border-color: var(--fair-rd-line-hairline);
}

.color-selection-step__item--selected {
  border-color: var(--fair-rd-primary);
  background: var(--fair-rd-fill-chip);
}

/* Swatch keeps a hairline so white and near-white stay visible. */
.color-selection-step__swatch {
  display: flex;
  width: 56px;
  height: 56px;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--fair-rd-line-hairline);
  border-radius: var(--fair-rd-radius-full);
  color: var(--fair-rd-text-on-dark);
  mix-blend-mode: normal;
}

.color-selection-step__name {
  font-family: var(--fair-font-body, Inter, sans-serif);
  font-size: 13px;
  line-height: 1.2;
  text-align: center;
  color: var(--fair-rd-text-secondary);
}

@media (prefers-reduced-motion: reduce) {
  .color-selection-step__item {
    transition: none;
  }
}
</style>
